/**
 * QAPanel — D2a: Pytania i odpowiedzi zamawiającego.
 *
 * Manualna wklejka z portalu BZP do czasu auto-scrapera (Playwright).
 * Każde Q&A doprecyzowuje wymagania → wgląd w intencje + miscalculations konkurencji.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import {
  MessageCircleQuestion, Plus, Trash2, Edit, AlertTriangle,
  Calendar, ExternalLink, FileEdit, X, Bot, Loader2,
} from 'lucide-react';
import { listTenderQa, addTenderQa, updateTenderQa, deleteTenderQa, autoScrapeTenderQa } from '../../lib/api';

const EMPTY_FORM = {
  question: '',
  answer: '',
  posted_by: 'zamawiający',
  posted_at: '',
  source_url: '',
  is_modification: false,
};

function QAItem({ item, onEdit, onDelete }) {
  return (
    <Card className={`border ${item.is_modification ? 'border-amber-500/50 bg-amber-500/5' : ''}`}>
      <CardContent className="pt-4 pb-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {item.is_modification && (
              <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Sprostowanie / modyfikacja
              </Badge>
            )}
            {item.posted_by && (
              <Badge variant="outline" className="text-xs">{item.posted_by}</Badge>
            )}
            {item.posted_at && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {new Date(item.posted_at).toLocaleString('pl-PL', {
                  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                })}
              </span>
            )}
            {item.source_url && (
              <a
                href={item.source_url} target="_blank" rel="noopener noreferrer"
                className="text-xs text-primary hover:underline flex items-center gap-0.5"
              >
                <ExternalLink className="w-3 h-3" /> źródło
              </a>
            )}
          </div>
          <div className="flex gap-1 shrink-0">
            <Button variant="ghost" size="sm" onClick={() => onEdit(item)} className="h-7 px-2">
              <Edit className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onDelete(item.id)} className="h-7 px-2 text-red-400">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Pytanie</div>
          <p className="text-sm whitespace-pre-wrap">{item.question}</p>
        </div>
        {item.answer && (
          <div className="border-t pt-2 mt-2">
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Odpowiedź zamawiającego</div>
            <p className="text-sm whitespace-pre-wrap text-foreground">{item.answer}</p>
          </div>
        )}
        {!item.answer && (
          <div className="text-xs text-muted-foreground italic">— brak odpowiedzi —</div>
        )}
      </CardContent>
    </Card>
  );
}

export default function QAPanel({ tenderId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [scraping, setScraping] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listTenderQa(tenderId);
      setItems(data.items || []);
    } catch (e) {
      toast.error('Błąd ładowania Q&A: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [tenderId]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (item) => {
    setForm({
      question: item.question || '',
      answer: item.answer || '',
      posted_by: item.posted_by || 'zamawiający',
      posted_at: item.posted_at || '',
      source_url: item.source_url || '',
      is_modification: !!item.is_modification,
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const startNew = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  };

  const cancel = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async () => {
    if (!form.question || form.question.trim().length < 3) {
      toast.error('Pytanie musi mieć min. 3 znaki');
      return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        await updateTenderQa(tenderId, editingId, form);
        toast.success('Q&A zaktualizowane');
      } else {
        await addTenderQa(tenderId, form);
        toast.success('Q&A dodane');
      }
      cancel();
      await load();
    } catch (e) {
      toast.error('Zapis nie powiódł się: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAutoScrape = async () => {
    setScraping(true);
    try {
      toast.info('Uruchamiam Playwright (Chromium) — może to zająć 10-30s...');
      const r = await autoScrapeTenderQa(tenderId);
      if (r.error) {
        toast.error('Auto-scrape nie powiódł się: ' + r.error.slice(0, 200));
      } else if (r.saved === 0) {
        toast.info(`Scraper przeanalizował stronę (${r.scraped} bloków znalezione), ale nie zapisano nic nowego (już istnieją lub brak Q&A na stronie).`);
      } else {
        toast.success(`Pobrano ${r.saved} nowych Q&A z BZP (z ${r.scraped} bloków)`);
      }
      await load();
    } catch (e) {
      toast.error('Błąd: ' + (e.response?.data?.detail || e.message));
    } finally {
      setScraping(false);
    }
  };

  const handleDelete = async (qaId) => {
    if (!window.confirm('Usunąć ten Q&A item?')) return;
    try {
      await deleteTenderQa(tenderId, qaId);
      toast.success('Usunięto');
      await load();
    } catch (e) {
      toast.error('Usuwanie nie powiodło się: ' + (e.response?.data?.detail || e.message));
    }
  };

  const modCount = items.filter(i => i.is_modification).length;

  return (
    <div className="space-y-3">
      {/* Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <MessageCircleQuestion className="w-4 h-4 text-primary" />
                Pytania i odpowiedzi zamawiającego
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                {items.length} pytań{modCount > 0 ? ` · ${modCount} sprostowań` : ''}.
                {' '}Wklej z portalu BZP, dopóki auto-scraper nie został uruchomiony.
              </p>
            </div>
            {!showForm && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleAutoScrape}
                  disabled={scraping}
                  title="Pobierz Q&A z e-Zamówienia przez Playwright (~15-30s)"
                >
                  {scraping
                    ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                    : <Bot className="w-3.5 h-3.5 mr-1" />}
                  Auto-pobierz z BZP
                </Button>
                <Button size="sm" onClick={startNew}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Dodaj ręcznie
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Form */}
      {showForm && (
        <Card className="border-primary/40">
          <CardContent className="pt-4 pb-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium flex items-center gap-2">
                <FileEdit className="w-3.5 h-3.5" />
                {editingId ? 'Edycja Q&A' : 'Nowy wpis Q&A'}
              </div>
              <Button variant="ghost" size="sm" onClick={cancel}>
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Wystawiający (np. zamawiający, firma X)"
                value={form.posted_by}
                onChange={e => setForm(p => ({ ...p, posted_by: e.target.value }))}
              />
              <Input
                type="datetime-local"
                value={form.posted_at?.slice(0, 16) || ''}
                onChange={e => setForm(p => ({ ...p, posted_at: e.target.value ? new Date(e.target.value).toISOString() : '' }))}
                placeholder="Data publikacji"
              />
            </div>
            <Textarea
              placeholder="Pytanie (skopiuj z portalu BZP)"
              value={form.question}
              onChange={e => setForm(p => ({ ...p, question: e.target.value }))}
              className="min-h-[80px]"
            />
            <Textarea
              placeholder="Odpowiedź zamawiającego (jeśli już udzielona)"
              value={form.answer}
              onChange={e => setForm(p => ({ ...p, answer: e.target.value }))}
              className="min-h-[100px]"
            />
            <Input
              placeholder="URL do oryginalnego ogłoszenia (opcjonalnie)"
              value={form.source_url}
              onChange={e => setForm(p => ({ ...p, source_url: e.target.value }))}
            />
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_modification}
                onChange={e => setForm(p => ({ ...p, is_modification: e.target.checked }))}
              />
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
              To jest sprostowanie / modyfikacja SWZ (nie zwykłe pytanie)
            </label>
            <div className="flex gap-2">
              <Button onClick={handleSubmit} disabled={submitting} size="sm">
                {submitting ? 'Zapisywanie...' : (editingId ? 'Zapisz zmiany' : 'Dodaj')}
              </Button>
              <Button variant="outline" onClick={cancel} size="sm" disabled={submitting}>
                Anuluj
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* List */}
      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="pt-8 pb-8 text-center text-muted-foreground">
            <MessageCircleQuestion className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Brak pytań i odpowiedzi.</p>
            <p className="text-xs mt-2">
              Wklej manualnie z portalu BZP — pytania konkurencji ujawniają luki w SWZ
              i wskazują w którym kierunku zamawiający dopuści odstępstwa.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map(item => (
            <QAItem
              key={item.id}
              item={item}
              onEdit={startEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}
